import React, { useEffect, useMemo, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Check, ChevronRight, Upload, User as UserIcon, Stethoscope, IdCard } from "lucide-react";
import StaffCommissionStep, { hydrateCommissionMap, serializeCommissionMap } from "./StaffCommissionStep";

// Iter-302 — Role hierarchy constants live in AuthEngine.jsx (the
// single source of truth for security logic). We re-export here so
// pre-Iter-302 call sites that import from `BrandStaffWizard` keep
// working; new code should import from `@/lib/AuthEngine`.
import { CARE_TEAM_ROLE_ID, MANAGEMENT_ROLE_IDS } from "@/lib/AuthEngine";
export { CARE_TEAM_ROLE_ID, MANAGEMENT_ROLE_IDS };

const emptyForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
  backup_email: "",
  whatsapp: "",
  gender: "",
  role_id: "",
  active: true,
  photo_url: "",
  branch_ids: [],
  service_ids: [],
  position: "",
});

export const POSITIONS = ["Doctor", "Nurse", "Therapist", "Driver", "Hair Stylist"];

/** Hydrate the wizard's local form state from an existing staff_user
 * document — used when the operator hits the "Edit" pencil on any
 * row in the Brand Staff table so the SAME 2-step wizard is reused
 * for updates. Jun 2026: employees sign in with a one-time code sent
 * to their email, so email is the only credential on this form.
 */
const hydrateFromUser = (u) => {
  const [first, ...rest] = (u.full_name || "").split(" ");
  return {
    first_name: u.first_name || first || "",
    last_name: u.last_name || rest.join(" ") || "",
    email: u.email || "",
    backup_email: u.backup_email || "",
    whatsapp: u.whatsapp || "",
    gender: u.gender || "",
    role_id: u.role_id || "",
    active: u.active !== false,
    photo_url: u.photo_url || "",
    branch_ids: [...(u.branch_ids || [])],
    service_ids: [...(u.service_ids || [])],
    position: u.position || "",
  };
};

/**
 * Global Brand-level staff registry wizard. Two dynamic steps:
 *  1. Identity — full name, credentials, gender, role, active toggle,
 *     photo, and branch access checklist.
 *  2. Specialization — services trained. Rendered ONLY when the role
 *     selected in step 1 is the Care Team preset (id equals
 *     `CARE_TEAM_ROLE_ID`); management roles skip straight to Save.
 *
 * On save the backend auto-seeds a 7-day "Day Off" schedule for Care
 * Team hires and files them under every assigned branch's Schedule
 * page — the toast prompts the operator to visit Branch Settings and
 * fill in real hours.
 */
export default function BrandStaffWizard({
  open,
  onOpenChange,
  branches,
  roles,
  categories,
  services,
  actorLevel,
  isActorOwner,
  editing,
  onSaved,
}) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [commissionMap, setCommissionMap] = useState({});
  const [commissionLoading, setCommissionLoading] = useState(false);
  const isEdit = !!editing;

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(editing ? hydrateFromUser(editing) : emptyForm());
      setCommissionMap({});
    }
  }, [open, editing]);

  const rolesForAssignment = useMemo(
    () =>
      roles.filter((r) => isActorOwner || (!r.is_owner && (r.hierarchy_level ?? 0) <= actorLevel)),
    [roles, isActorOwner, actorLevel]
  );
  const roleById = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);

  const isCareTeamRole = form.role_id === CARE_TEAM_ROLE_ID;
  const isManagementRole = MANAGEMENT_ROLE_IDS.has(form.role_id);
  const totalSteps = isCareTeamRole ? 3 : 1;
  const stepLabels = isCareTeamRole
    ? ["Identity & Access", "Specialization", "Commission"]
    : ["Identity & Access"];

  // Fetch existing commission rules when the operator opens Edit on a
  // Care-Team profile. Fresh profiles start with an empty map. We also
  // reload if the role flips to Care Team mid-wizard.
  useEffect(() => {
    if (!open || !isCareTeamRole || !editing?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setCommissionLoading(true);
        const r = await staffApi.get(`/staff-users/${editing.id}/commissions`);
        if (!cancelled) setCommissionMap(hydrateCommissionMap(r.data?.rules || []));
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setCommissionLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isCareTeamRole, editing?.id]);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await staffApi.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: r.data.url }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  // Email is MANDATORY — it is the employee's only way to sign in (a
  // one-time code is emailed to it). `emailError` surfaces the exact
  // reason inline on the Identity step.
  const emailError = (() => {
    const email = (form.email || "").trim();
    if (!email) return "Email is required — employees sign in with a one-time code sent to this address.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address (e.g. name@cocoonmedicalspa.com).";
    return "";
  })();

  const canProceedStep1 = () => {
    if (!form.first_name.trim()) return false;
    if (emailError) return false;
    if (!form.role_id) return false;
    if (form.branch_ids.length === 0) return false;
    return true;
  };

  const submit = async () => {
    if (emailError) {
      setStep(1);
      toast.error(emailError);
      return;
    }
    if (!canProceedStep1()) {
      setStep(1);
      toast.error("Please complete every required field on the Identity step.");
      return;
    }
    setBusy(true);
    try {
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const email = form.email.trim().toLowerCase();
      const backup_email = (form.backup_email || "").trim().toLowerCase();
      const whatsapp = form.whatsapp.trim() || null;
      let savedId;
      if (isEdit) {
        const payload = {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          full_name: fullName,
          email,
          backup_email,
          whatsapp,
          gender: form.gender || null,
          photo_url: form.photo_url || "",
          role_id: form.role_id,
          branch_ids: form.branch_ids,
          active: form.active,
          service_ids: isCareTeamRole ? form.service_ids : [],
          position: form.position || null,
        };
        await staffApi.put(`/staff-users/${editing.id}`, payload);
        savedId = editing.id;
        toast.success("Staff profile updated.");
      } else {
        const payload = {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          full_name: fullName,
          email,
          backup_email: backup_email || null,
          whatsapp,
          gender: form.gender || null,
          photo_url: form.photo_url || "",
          role: "staff",
          role_id: form.role_id,
          branch_ids: form.branch_ids,
          active: form.active,
          service_ids: isCareTeamRole ? form.service_ids : [],
          position: form.position || null,
        };
        const r = await staffApi.post("/staff-users", payload);
        savedId = r?.data?.id;
        toast.success("Staff profile created. Please go to Branch Settings to set their opening hours.");
      }
      // Persist commission rules for Care-Team profiles. Non Care-Team
      // roles never see Step 3 so there's nothing to save.
      if (isCareTeamRole && savedId) {
        try {
          await staffApi.put(`/staff-users/${savedId}/commissions`, {
            rules: serializeCommissionMap(commissionMap),
          });
        } catch (e) {
          toast.error(`Commission save failed: ${formatApiError(e)}`);
        }
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const roleHint = form.role_id
    ? isCareTeamRole
      ? "Care Team practitioners appear on the booking calendar once you tick at least one service."
      : isManagementRole
      ? "Management roles never appear on the calendar or schedule pages."
      : "Custom role — will list under the Care Team table."
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="brand-staff-wizard">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{isEdit ? "Edit staff profile" : "New staff profile"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update identity, access, and (for Care Team) trained services. Their email is how they sign in — we send a one-time code to it."
              : "One global record — assign the branches, role, and (for Care Team) trained services."}
          </DialogDescription>
        </DialogHeader>

        <ol className="flex items-center gap-2 px-6 pb-4 border-b border-border overflow-x-auto">
          {stepLabels.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <li key={label} className="flex items-center gap-2 text-xs shrink-0">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${
                    active ? "bg-primary text-primary-foreground" : done ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {done ? <Check className="w-3 h-3" /> : n}
                </span>
                <span className={active ? "font-medium" : "text-muted-foreground"}>{label}</span>
                {n < totalSteps && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
              </li>
            );
          })}
        </ol>

        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
          {step === 1 && (
            <IdentityStep
              form={form}
              setForm={setForm}
              rolesForAssignment={rolesForAssignment}
              roleById={roleById}
              actorLevel={actorLevel}
              branches={branches}
              uploadPhoto={uploadPhoto}
              uploading={uploading}
              roleHint={roleHint}
              isEdit={isEdit}
              emailError={emailError}
            />
          )}
          {step === 2 && isCareTeamRole && (
            <SpecializationStep form={form} setForm={setForm} categories={categories} services={services} />
          )}
          {step === 3 && isCareTeamRole && (
            commissionLoading ? (
              <p className="text-sm text-muted-foreground" data-testid="commission-step-loading">Loading commission rules…</p>
            ) : (
              <StaffCommissionStep
                value={commissionMap}
                onChange={setCommissionMap}
                categories={categories}
                services={services}
                specializedServiceIds={form.service_ids}
                currencyLabel="IDR"
              />
            )
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-secondary/40">
          <Button variant="ghost" onClick={() => (step === 1 ? onOpenChange(false) : setStep(step - 1))} data-testid="wizard-back-btn">
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceedStep1()}
              className="rounded-full"
              data-testid="wizard-next-btn"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={busy || !canProceedStep1()}
              className="rounded-full"
              data-testid="wizard-finish-btn"
            >
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create staff profile"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Step 1 · Identity & Access ---------------- */
function IdentityStep({ form, setForm, rolesForAssignment, roleById, actorLevel, branches, uploadPhoto, uploading, roleHint, isEdit, emailError }) {
  const toggleBranch = (id) =>
    setForm((f) => ({
      ...f,
      branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((x) => x !== id) : [...f.branch_ids, id],
    }));
  const selectedRole = roleById[form.role_id];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <IdCard className="w-3.5 h-3.5" /> Identity
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>First name <span className="text-destructive">*</span></Label>
          <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="staff-first-name" autoFocus />
        </div>
        <div>
          <Label>Last name</Label>
          <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="staff-last-name" />
        </div>
        {/* Jun 2026 · Email is the employee's SIGN-IN identity — a
             one-time code is emailed to it, so it is mandatory and must
             be unique. Whatsapp stays optional and is saved verbatim so
             operators can paste any regional format. */}
        <div>
          <Label>
            Email address <span className="text-destructive">*</span>
          </Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="staff-email"
            placeholder="name@cocoonmedicalspa.com"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            name="cocoon-staff-email-field"
            className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
          />
          {emailError ? (
            <p className="text-[11px] text-destructive mt-0.5" data-testid="staff-email-error">{emailError}</p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              This is how they sign in — we email a one-time code to this address.
            </p>
          )}
        </div>
        <div>
          <Label>
            Whatsapp{" "}
            <span className="text-[11px] text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Input
            type="tel"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            data-testid="staff-whatsapp"
            placeholder="+62 812 3456 7890"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Gender</Label>
          <RadioGroup
            value={form.gender}
            onValueChange={(v) => setForm({ ...form, gender: v })}
            className="flex gap-6 mt-2"
            data-testid="staff-gender"
          >
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="male" data-testid="staff-gender-male" /> Male
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="female" data-testid="staff-gender-female" /> Female
            </label>
          </RadioGroup>
        </div>
        <div>
          <Label>Status</Label>
          <RadioGroup
            value={form.active ? "active" : "inactive"}
            onValueChange={(v) => setForm({ ...form, active: v === "active" })}
            className="flex gap-6 mt-2"
            data-testid="staff-status"
          >
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="active" data-testid="staff-status-active" /> Active
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value="inactive" data-testid="staff-status-inactive" /> Inactive
            </label>
          </RadioGroup>
        </div>
      </div>

      <div>
        <Label>Role <span className="text-destructive">*</span></Label>
        <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
          <SelectTrigger data-testid="staff-role-select" className="mt-1"><SelectValue placeholder="Choose a role" /></SelectTrigger>
          <SelectContent>
            {rolesForAssignment.map((r) => (
              <SelectItem key={r.id} value={r.id} data-testid={`staff-role-option-${r.id}`}>
                {r.name} · Level {r.hierarchy_level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground mt-1">
          You can only assign roles at or below your own hierarchy level ({actorLevel}).
          {selectedRole && <span className="block mt-0.5">{roleHint}</span>}
        </p>
      </div>

      <div>
        <Label>Position / Discipline</Label>
        <Select value={form.position || ""} onValueChange={(v) => setForm({ ...form, position: v })}>
          <SelectTrigger data-testid="staff-position-select" className="mt-1"><SelectValue placeholder="Choose a position" /></SelectTrigger>
          <SelectContent>
            {POSITIONS.map((p) => (
              <SelectItem key={p} value={p} data-testid={`staff-position-option-${p}`}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground mt-1">
          Drives clinical-record responsibility (Doctor &gt; Nurse &gt; Therapist). Drivers and Hair Stylists carry no medical-record duty.
        </p>
      </div>

      <div>
        <Label>Photo</Label>
        <div className="flex items-center gap-4 mt-2">
          <div className="w-20 h-20 rounded-full bg-secondary overflow-hidden flex items-center justify-center shrink-0">
            {form.photo_url ? (
              <img src={form.photo_url} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
              data-testid="staff-photo-input"
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : form.photo_url ? "Replace photo" : "Upload photo"}
            </span>
          </label>
          {form.photo_url && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setForm((f) => ({ ...f, photo_url: "" }))}
              className="text-destructive hover:text-destructive"
              data-testid="staff-photo-clear"
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div>
            <Label className="text-sm font-semibold">Branch access <span className="text-destructive">*</span></Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Tick every branch this staff member can operate at. Care Team members get a &quot;Day Off&quot; schedule seeded at each ticked branch.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button type="button" size="sm" variant="ghost" onClick={() => setForm((f) => ({ ...f, branch_ids: branches.map((b) => b.id) }))} data-testid="staff-branch-select-all">
              All
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setForm((f) => ({ ...f, branch_ids: [] }))} data-testid="staff-branch-clear-all">
              None
            </Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {branches.map((b) => (
            <label
              key={b.id}
              className={`flex items-center gap-2 text-sm border rounded-md p-2 cursor-pointer hover:bg-muted transition-colors ${
                form.branch_ids.includes(b.id) ? "border-primary bg-primary/5" : "border-border"
              }`}
            >
              <Checkbox
                checked={form.branch_ids.includes(b.id)}
                onCheckedChange={() => toggleBranch(b.id)}
                data-testid={`staff-branch-${b.id}`}
              />
              <span className="min-w-0 truncate">{b.name}</span>
            </label>
          ))}
          {branches.length === 0 && (
            <p className="col-span-2 text-xs text-muted-foreground">No branches available yet.</p>
          )}
        </div>
        {form.branch_ids.length === 0 && (
          <p className="text-[11px] text-amber-700 mt-2" data-testid="staff-branch-empty-warning">
            ⚠︎ Pick at least one branch — this staff member won&apos;t be able to sign in anywhere until you do.
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Step 2 · Specialization (Care Team only) ---------------- */
function SpecializationStep({ form, setForm, categories, services }) {
  // Smart Staff-Service Filter (Feb 2026): strictly narrow the list to
  // services offered at any of the branches this staff member is
  // assigned to in Step 1. Services with an empty (or missing)
  // `branch_ids` are "available at every branch" and always pass.
  // Recomputes on every render so a Step-1 branch toggle instantly
  // re-scopes Step 2 without any extra state — the effect below prunes
  // any newly-invalid ids from the current selection.
  const branchSet = React.useMemo(() => new Set(form.branch_ids || []), [form.branch_ids]);
  const filteredServices = React.useMemo(() => {
    if (branchSet.size === 0) return [];
    return services.filter((s) => {
      const offered = s.branch_ids || [];
      if (offered.length === 0) return true; // available everywhere
      return offered.some((bid) => branchSet.has(bid));
    });
  }, [services, branchSet]);

  // Data-sync: whenever the filtered list changes, drop any selected
  // service_id that's no longer offered at the assigned branches so the
  // wizard never submits stale training.
  const filteredIds = React.useMemo(() => new Set(filteredServices.map((s) => s.id)), [filteredServices]);
  useEffect(() => {
    const kept = (form.service_ids || []).filter((id) => filteredIds.has(id));
    if (kept.length !== (form.service_ids || []).length) {
      setForm((f) => ({ ...f, service_ids: kept }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredIds]);

  const svcByCat = useMemo(() => {
    const map = new Map();
    for (const s of filteredServices) {
      const cid = s.category_id || "__none__";
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(s);
    }
    return map;
  }, [filteredServices]);

  const selected = new Set(form.service_ids);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setForm({ ...form, service_ids: [...next] });
  };
  const bulkSet = (ids, on) => {
    const next = new Set(selected);
    for (const id of ids) {
      if (on) next.add(id); else next.delete(id);
    }
    setForm({ ...form, service_ids: [...next] });
  };
  const catById = new Map(categories.map((c) => [c.id, c]));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Stethoscope className="w-3.5 h-3.5" /> Trained services
      </div>
      <p className="text-xs text-muted-foreground">
        Tick every service this Care Team member is trained to deliver. Their name only lists on the booking calendar once at least one service is ticked — leaving it empty keeps the profile hidden until you&apos;re ready.
      </p>
      <p className="text-[11px] text-muted-foreground rounded-md border border-border bg-secondary/40 px-3 py-2" data-testid="specialization-filter-hint">
        Showing only services active in the {branchSet.size} branch{branchSet.size === 1 ? "" : "es"} selected on Step 1. Change the branch mix on Step 1 and this list will refresh instantly.
      </p>

      <Accordion type="multiple" defaultValue={[...svcByCat.keys()]} className="space-y-2">
        {[...svcByCat.entries()].map(([cid, list]) => {
          const cat = catById.get(cid);
          const catName = cat ? cat.name : "Uncategorised";
          const catIds = list.map((s) => s.id);
          const allSelected = catIds.every((id) => selected.has(id));

          return (
            <AccordionItem key={cid} value={cid} className="rounded-lg border border-border">
              <div className="flex items-center pr-3">
                <AccordionTrigger className="px-3 py-2.5 hover:no-underline flex-1">
                  <span className="text-sm font-medium">
                    {catName}
                    <span className="ml-2 text-[11px] text-muted-foreground font-normal">
                      {catIds.filter((id) => selected.has(id)).length}/{catIds.length}
                    </span>
                  </span>
                </AccordionTrigger>
                <div className="flex items-center gap-2 text-[11px] shrink-0">
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => bulkSet(catIds, true)}
                    data-testid={`svc-cat-check-all-${cid}`}
                    disabled={allSelected}
                  >
                    Check all
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:underline"
                    onClick={() => bulkSet(catIds, false)}
                    data-testid={`svc-cat-uncheck-all-${cid}`}
                  >
                    Uncheck
                  </button>
                </div>
              </div>
              <AccordionContent className="px-3 pb-3">
                <div className="grid sm:grid-cols-2 gap-2">
                  {list.map((s) => (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 text-sm border rounded-md p-2 cursor-pointer hover:bg-muted transition-colors ${
                        selected.has(s.id) ? "border-primary bg-primary/5" : "border-border"
                      }`}
                    >
                      <Checkbox
                        checked={selected.has(s.id)}
                        onCheckedChange={() => toggle(s.id)}
                        data-testid={`svc-${s.id}`}
                      />
                      <span className="min-w-0 truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
        {svcByCat.size === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="specialization-empty">
            {branchSet.size === 0
              ? "Assign at least one branch on Step 1 before picking trained services."
              : "No services available at the selected branch(es) yet — add them under Brand → Services and scope them to this location."}
          </p>
        )}
      </Accordion>
    </div>
  );
}
